import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  dismissHelpHint,
  setHelpContext,
  type ContextId,
} from '../../store/slices/helpSlice';
import { requestNavigateMainTab } from '../../store/slices/modalsSlice';
import useAuth from '../../hooks/useAuth';
import { getContextHints } from '../../lib/help/helpContexts';
import {
  getDismissedHelpHints,
  markHelpHintDismissed,
} from '../../lib/help/helpPersistence';
import { trackHelpEvent } from '../../lib/help/helpAnalytics';
import { HelpCircle, X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Backwards-compatible alias for callers that imported the old refined union.
// The hint component now accepts any ContextId - Wave 1 widened the type system
// so chart_view / deep_analysis are aliases rather than distinct TabIds.
export type HintableContext = ContextId;

interface ContextHelpHintProps {
  context: ContextId;
  className?: string;
}

const ContextHelpHint: React.FC<ContextHelpHintProps> = ({ context, className }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useAuth();
  const dismissedHints = useAppSelector((state) => state.help.dismissedHelpHints);
  const persistedDismissedHints = getDismissedHelpHints(user);
  const hint = useMemo(
    () =>
      getContextHints(context as ContextId).find(
        (candidate) => !dismissedHints[candidate.id] && !persistedDismissedHints[candidate.id]
      ),
    [context, dismissedHints, persistedDismissedHints]
  );

  useEffect(() => {
    if (!hint) return;
    // Mark as seen immediately so each user only gets this hint once.
    markHelpHintDismissed(user, hint.id);
    trackHelpEvent('context_hint_shown', { context, hintId: hint.id });
  }, [context, hint, user]);

  if (!hint) return null;

  const hintTitle = t(`help.hints.${hint.id}.title`, { defaultValue: hint.title });
  const hintContent = t(`help.hints.${hint.id}.content`, { defaultValue: hint.content });

  return (
    <aside
      className={`rounded-lg border border-border-subtle bg-surface-container-low px-3 py-2 text-xs text-text-secondary ${className ?? ''}`}
      aria-label={`${t('help.open', { defaultValue: 'Help' })} ${context}`}
    >
      <div className="flex items-start gap-2">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 font-semibold text-text-primary break-words">{hintTitle}</div>
          <p className="m-0 break-words leading-snug">{hintContent}</p>
          <button
            type="button"
            className="mt-1 text-accent-primary underline underline-offset-2"
            onClick={() => {
              dispatch(setHelpContext(context));
              dispatch(requestNavigateMainTab('help'));
              trackHelpEvent('help_topic_viewed', {
                context,
                hintId: hint.id,
                topicId: hint.topicId ?? null,
                source: 'context_hint',
              });
            }}
          >
            {t('help.learnMore', { defaultValue: 'Learn more' })}
          </button>
        </div>
        <button
          type="button"
          className="rounded p-1 text-text-muted hover:text-text-primary"
          onClick={() => {
            markHelpHintDismissed(user, hint.id);
            dispatch(dismissHelpHint(hint.id));
            trackHelpEvent('context_hint_dismissed', { context, hintId: hint.id });
          }}
          aria-label={t('help.dismissHint', { defaultValue: 'Dismiss help hint' })}
          title={t('help.dismissHint', { defaultValue: 'Dismiss help hint' })}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
};

export default ContextHelpHint;
